import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useTaskNotifications } from "./useTaskNotifications";

export const useTaskForm = (
  editingTaskId: string | null | undefined,
  onClose: () => void,
  onTaskCreated: () => void,
  isAdmin: boolean
) => {
  const { toast } = useToast();
  const { createNotification } = useTaskNotifications();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    assigned_to: "",
    date: "",
    location: "",
    priority: "medium",
    status: "pending",
    estimated_hours: 1,
    actual_hours: 0,
    task_type: "installation",
    created_by_type: (isAdmin ? 'admin' : 'employee') as 'admin' | 'employee'
  });

  useEffect(() => {
    if (editingTaskId) {
      fetchTaskDetails();
    }
  }, [editingTaskId]);

  const fetchTaskDetails = async () => {
    if (!editingTaskId) return;

    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('id', editingTaskId)
        .single();

      if (error) throw error;

      if (data) {
        setFormData(data);
      }
    } catch (error) {
      console.error('Fejl ved hentning af opgavedetaljer:', error);
      toast({
        title: "Fejl ved hentning",
        description: "Kunne ikke hente opgavedetaljerne.",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const submitData = {
        ...formData,
        created_by_type: isAdmin ? 'admin' : 'employee' as 'admin' | 'employee',
      };

      if (editingTaskId) {
        const { error, data } = await supabase
          .from('tasks')
          .update(submitData)
          .eq('id', editingTaskId)
          .select()
          .single();

        if (error) throw error;

        await createNotification(editingTaskId, formData.assigned_to, formData.title, true);

        toast({
          title: "Opgave opdateret",
          description: "Opgaven er blevet opdateret succesfuldt."
        });
      } else {
        const { error, data } = await supabase
          .from('tasks')
          .insert([submitData])
          .select()
          .single();

        if (error) throw error;

        if (data) {
          await createNotification(data.id, formData.assigned_to, formData.title);
        }

        toast({
          title: "Opgave oprettet",
          description: "Den nye opgave er blevet oprettet succesfuldt."
        });
      }
      
      onTaskCreated();
      onClose();
    } catch (error) {
      console.error('Fejl ved håndtering af opgave:', error);
      toast({
        title: `Fejl ved ${editingTaskId ? 'opdatering' : 'oprettelse'}`,
        description: `Der skete en fejl ved ${editingTaskId ? 'opdatering' : 'oprettelse'} af opgaven. Prøv igen.`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    formData,
    setFormData,
    handleSubmit
  };
};